/**
 * Build a full `palette.brand` ramp from ONE colour — the tenant's Studio
 * `primary_color`.
 *
 * ## The bug this exists to fix
 *
 * `apps/app` mounts two themes. The outer static one (`settings/theme.ts`) and
 * the Studio-driven one (`settings/mui/createMuiTheme.ts`) both resolve
 * `primary`, `background`, `text` and `shape` — but NEITHER defines `brand`, so
 * `palette.brand[*]` fell through to `@repo/ui`'s ramp, which is Vera's purple.
 * On every tenant.
 *
 * That is not cosmetic. `@repo/ui`'s `Input` styles its hover and focus border
 * from `theme.palette.brand[600]`, so **every text field in the customer app
 * focus-ringed Vera purple on every brand**, inside that brand's own site. The
 * codebase already knew (`PhoneVerificationDrawer.tsx` carries a VERA-291
 * comment describing exactly this) but it was patched at one call site.
 *
 * ## Why derive the ramp instead of banning `brand.*`
 *
 * The alternative was to treat `brand` as Vera-only and rewrite every consumer
 * onto `primary`. That does not reach `@repo/ui`'s internals without either a
 * new prop on every component or a fork, and it leaves the token meaning
 * "Vera's purple" in an app where nothing should be Vera-coloured.
 *
 * Deriving instead makes the token mean what its name says: **`brand` is the
 * brand THIS UI represents.** In `apps/platform` that is Vera, so `@repo/ui`'s
 * ramp is already correct and is untouched by this file. In `apps/app` it is
 * the tenant, so it is derived here. One consistent reading, and every existing
 * `brand[600]` consumer — ours and `@repo/ui`'s — becomes correct for free.
 *
 * ## How
 *
 * Mix ratios are measured from `@repo/ui`'s purple ramp rather than invented:
 * for each step, the `t` that best reproduces it as a linear mix of `600`
 * toward white (lighter steps) or black (darker steps). Max reconstruction
 * error is 15/255, so a derived ramp keeps the original's perceptual shape.
 *
 * An achromatic source degrades correctly on its own: black yields a pure grey
 * ramp, which is exactly the neutral treatment an unthemed tenant should get.
 */

/** Measured against `@repo/ui`'s brand ramp — see the module comment. */
const MIX: Record<number, number> = {
  50: 0.946,
  100: 0.888,
  200: 0.806,
  300: 0.647,
  400: 0.438,
  500: 0.215,
  600: 0,
  700: 0.155,
  800: 0.293,
  900: 0.41,
  950: 0.594,
};

type Rgb = [number, number, number];

const parseHex = (hex: string): Rgb | null => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
};

const toHex = ([r, g, b]: Rgb): string =>
  '#' + [r, g, b].map(v => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('').toUpperCase();

const mix = (from: Rgb, toward: 0 | 255, t: number): Rgb =>
  [0, 1, 2].map(i => from[i] + t * (toward - from[i])) as Rgb;

export interface BrandRamp {
  50: string; 100: string; 200: string; 300: string; 400: string; 500: string;
  600: string; 700: string; 800: string; 900: string; 950: string;
  /** The soft focus ring `@repo/ui` Input paints behind a focused border. */
  '600-6%': string;
  '600-32%': string;
  /**
   * Did a TENANT supply this ramp's source colour, or is this the base Vera
   * theme's neutral fallback?
   *
   * This is NOT a new check. It is the SAME condition the ramp itself already
   * branches on one line below — `(source && parseHex(source)) || [0, 0, 0]` —
   * simply named and published, because the fallback is otherwise lossy: once
   * the ramp is built, "no brand colour configured" and "a brand whose colour
   * is black" are the same eleven strings, and nothing downstream can tell them
   * apart.
   *
   * That distinction matters to any chrome that FILLS itself with the brand.
   * `CountBadge` is the first consumer: on a tenant it paints `brand[600]`, and
   * on the base theme it paints Vera's own purple gradient rather than the
   * black the neutral ramp would give it. Black there represented nobody — the
   * same objection `settings/theme.ts` records against the old purple default,
   * pointed the other way.
   *
   * ⚠️ One caller reports `true` where a reader might expect `false`:
   * `settings/theme.ts` builds the OUTER static theme with a hardcoded
   * `brandRamp('#000000')`, which parses, so that layer's ramp claims to be
   * tenant-derived. It is harmless today — that theme is replaced by the
   * Studio-driven one (`createMuiTheme`) for everything App renders, and no
   * brand-filled chrome mounts above it — but if a consumer of this flag ever
   * renders outside `App`'s `ThemeProvider`, that call should become
   * `brandRamp(null)`, which produces a byte-identical ramp and the honest flag.
   */
  isTenantDerived: boolean;
}

/**
 * @param source the tenant's `primary_color`. Anything unparseable falls back
 *   to black, which produces the neutral grey ramp — never Vera purple, since
 *   silently reverting to it is the exact failure this module removes.
 */
export const brandRamp = (source?: string | null): BrandRamp => {
  const parsed = source ? parseHex(source) : null;
  const base = parsed || [0, 0, 0];
  const step = (k: number) =>
    toHex(k === 600 ? base : mix(base, k < 600 ? 255 : 0, MIX[k]));
  const rgba = (a: number) => `rgba(${base[0]}, ${base[1]}, ${base[2]}, ${a})`;

  return {
    50: step(50), 100: step(100), 200: step(200), 300: step(300),
    400: step(400), 500: step(500), 600: step(600), 700: step(700),
    800: step(800), 900: step(900), 950: step(950),
    '600-6%': rgba(0.06),
    '600-32%': rgba(0.32),
    isTenantDerived: parsed !== null,
  };
};
